import { useId } from 'react';
import { TRACK_COLOR, toneColor } from './tokens';
import { clamp } from './scale';

// =========================================================================
// RingGauge — the demo's `ring(pct, state)` (source line 2364). Fork-owned.
//
// One arc on one track, drawn with `stroke-dasharray` on a circle rather than
// an arc path: a dasharray sweep is exact at 0% and at 100%, where the two
// endpoints of a hand-built `A` path land on the same coordinate and the
// renderer draws nothing.
//
// The figure in the middle is the value. The ring is the context. If the ring
// were the only carrier of the number this would be a chart that cannot be
// read aloud — so the centre label is always present and always text.
// =========================================================================

export interface RingStatProps {
  /** 0–100. Values outside the range are clamped, not wrapped. */
  pct: number;
  /** Centre label. Defaults to the rounded percentage. */
  label?: string;
  /** Small line under the centre label. */
  sub?: string;
  /** Engine tone token: `good` | `warn` | `bad` | `flat`. */
  tone?: string;
  /** Rendered diameter in CSS pixels. */
  size?: number;
  /** Accessible name — what the percentage is *of*. */
  title: string;
}

/** viewBox is fixed; `size` only scales the rendered box. */
const VIEW = 100;
const RADIUS = 42;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export function RingStat({
  pct,
  label,
  sub,
  tone = 'flat',
  size = 84,
  title,
}: RingStatProps) {
  const titleId = useId();
  const value = clamp(pct, 0, 100);
  const dash = (value / 100) * CIRCUMFERENCE;

  return (
    <svg
      viewBox={`0 0 ${VIEW} ${VIEW}`}
      width={size}
      height={size}
      role="img"
      aria-labelledby={titleId}
      className="flex-none"
    >
      <title id={titleId}>{`${title}: ${Math.round(value)}%`}</title>

      <circle
        cx={VIEW / 2}
        cy={VIEW / 2}
        r={RADIUS}
        fill="none"
        stroke={TRACK_COLOR}
        strokeWidth={8}
      />
      <circle
        cx={VIEW / 2}
        cy={VIEW / 2}
        r={RADIUS}
        fill="none"
        stroke={toneColor(tone)}
        strokeWidth={8}
        strokeLinecap="round"
        strokeDasharray={`${dash} ${CIRCUMFERENCE - dash}`}
        // Start at 12 o'clock rather than 3 o'clock.
        transform={`rotate(-90 ${VIEW / 2} ${VIEW / 2})`}
      />

      <text
        x={VIEW / 2}
        y={sub ? VIEW / 2 : VIEW / 2 + 6}
        textAnchor="middle"
        fontSize={sub ? 21 : 23}
        fontWeight={600}
        fill="var(--ink-primary)"
        className="mono"
      >
        {label ?? `${Math.round(value)}%`}
      </text>
      {sub ? (
        <text
          x={VIEW / 2}
          y={VIEW / 2 + 16}
          textAnchor="middle"
          fontSize={10}
          fill="var(--ink-tertiary)"
        >
          {sub}
        </text>
      ) : null}
    </svg>
  );
}
